<template>
  <div class="page-container">
    <header class="page-header">
      <h1 class="header-title">院校参赛作品管理</h1>
      <div class="header-subtitle">
        <div class="left">
          <span v-for="(item, index) in schools" :key="index">{{ item.title }}</span>
        </div>
        <div class="right" @click="handleLogout">退出登录</div>
      </div>
    </header>

    <main class="page-content">
      <a-card class="search-card">
        <a-form layout="inline" class="search-form">
          <a-form-item label="作品名称">
            <a-input
              v-model.trim="params.name"
              placeholder="请输入作品名称关键词"
              style="width: 220px"
              @pressEnter="handleSearch"
            />
          </a-form-item>
          <a-form-item label="作者姓名">
            <a-input
              v-model.trim="params.realName"
              placeholder="请输入作者姓名"
              style="width: 160px"
              @pressEnter="handleSearch"
            />
          </a-form-item>
          <a-form-item>
            <a-button type="primary" @click="handleSearch">搜索</a-button>
          </a-form-item>
        </a-form>
      </a-card>

      <a-card class="table-card">
        <div class="table-wrapper">
          <a-table
            :columns="columns"
            :data-source="listData"
            :pagination="false"
            :scroll="{ x: 1600 }"
            bordered
            size="small"
            row-key="id"
          >
            <template slot="plateId" slot-scope="text, record">
              <span>{{ getPlateText(record.plateId) }}</span>
            </template>
            <template slot="categoryId" slot-scope="text, record">
              <span>{{ getCategoryText(record.categoryId) }}</span>
            </template>
            <template slot="compete" slot-scope="text, record">
              <span>{{ getCompeteText(record.compete) }}</span>
            </template>
            <template slot="sort" slot-scope="text, record">
              <a-input
                :value="record.sort"
                style="width: 70px"
                @change="(event) => updateSort(record, event)"
              />
            </template>
            <template slot="action" slot-scope="text, record">
              <span class="action-link" @click="handleView(record.id)">查看</span>
            </template>
          </a-table>
        </div>

        <div class="pagination-wrapper">
          <a-pagination
            size="small"
            :total="total"
            :current="params.page"
            :page-size="params.limit"
            :show-total="(value) => `共 ${value} 条`"
            @change="onChange"
          />
        </div>
      </a-card>
    </main>

    <footer class="page-footer">
      <div class="footer-content">
        <div class="copyright">Copyright © 2025 艺科美课</div>
        <div class="tech-support">院校账号可在此查看并维护报名作品排序。</div>
      </div>
    </footer>
  </div>
</template>

<script>
import { getWorksList, schoolEditWorks } from "@/api/SchoolWorks";
import { authLogout } from "@/api/Login";
import toLogin from "@/utils/login";

const PLATE_TEXT_MAP = {
  11: "传统工艺美术",
  12: "现代工艺美术",
};

const CATEGORY_TEXT_MAP = {
  2: "金属工艺",
  3: "陶瓷工艺",
  4: "漆艺",
  5: "玻璃工艺",
  6: "纤维工艺",
  7: "编织工艺",
  8: "木雕工艺",
  9: "综合材料",
  10: "其他工艺",
};

const COMPETE_TEXT_MAP = {
  2: "个人参赛",
  3: "团队参赛",
};

export default {
  name: "SchoolWorks",
  data() {
    return {
      columns: [
        { title: "板块", dataIndex: "plateId", width: 90, scopedSlots: { customRender: "plateId" } },
        { title: "类别", dataIndex: "categoryId", width: 120, scopedSlots: { customRender: "categoryId" } },
        { title: "参赛形式", dataIndex: "compete", width: 100, scopedSlots: { customRender: "compete" } },
        { title: "排序", dataIndex: "sort", width: 80, scopedSlots: { customRender: "sort" } },
        { title: "作品名称", dataIndex: "name", width: 180, ellipsis: true },
        { title: "作品简介", dataIndex: "content", width: 240, ellipsis: true },
        { title: "作者", dataIndex: "realName", width: 100 },
        { title: "手机号", dataIndex: "mobile", width: 120 },
        { title: "指导老师", dataIndex: "mentor", width: 120 },
        { title: "材料", dataIndex: "material", width: 160, ellipsis: true },
        { title: "重量", dataIndex: "weight", width: 100 },
        { title: "尺寸", dataIndex: "worksSize", width: 160, ellipsis: true },
        { title: "操作", dataIndex: "action", fixed: "right", width: 80, scopedSlots: { customRender: "action" } },
      ],
      listData: [],
      schools: [],
      params: {
        limit: 10,
        page: 1,
        name: "",
        categoryId: 0,
        plateId: 0,
        realName: "",
      },
      total: 0,
      totalPage: 0,
    };
  },
  mounted() {
    this.getList(this.params);
  },
  methods: {
    handleLogout() {
      const currentPath = this.$route.fullPath;
      authLogout()
        .catch(() => null)
        .finally(() => {
          this.$store.commit("LOGOUT");
          localStorage.clear();
          toLogin(false, currentPath);
        });
    },
    handleSearch() {
      this.params.page = 1;
      this.getList(this.params);
    },
    updateSort(row, event) {
      const nextSort = Number(event?.target?.value);
      if (Number.isNaN(nextSort)) {
        this.$message.error("排序必须为数字");
        return;
      }
      schoolEditWorks({ ...row, sort: nextSort }).then(() => {
        row.sort = nextSort;
        this.$message.success(`${row.name} 排序已更新为 ${nextSort}`);
      });
    },
    onChange(page) {
      this.params.page = page;
      this.getList(this.params);
    },
    getList(params) {
      getWorksList(params).then((res) => {
        const data = res.data || {};
        this.listData = Array.isArray(data.list) ? data.list : [];
        this.schools = Array.isArray(data.school) ? data.school : [];
        this.total = Number(data.total || 0);
        this.totalPage = Number(data.totalPage || 0);
      });
    },
    handleView(id) {
      const targetRoute = this.$router.resolve({
        path: "/SchoolWorksDetail",
        query: { id },
      });
      window.open(targetRoute.href, "_blank");
    },
    getPlateText(plateId) {
      return PLATE_TEXT_MAP[plateId] || "其他板块";
    },
    getCategoryText(categoryId) {
      return CATEGORY_TEXT_MAP[categoryId] || "其他类别";
    },
    getCompeteText(compete) {
      return COMPETE_TEXT_MAP[compete] || "个人参赛";
    },
  },
};
</script>

<style lang="scss">
.page-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #f0f2f5;
}

.page-header {
  padding: 20px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.header-title {
  margin: 0;
  font-size: 24px;
  color: #1f1f1f;
}

.header-subtitle {
  margin-top: 8px;
  display: flex;
  justify-content: space-between;
  color: #666;
  font-size: 16px;

  .left {
    width: 800px;
  }

  .left span + span {
    margin-left: 12px;
  }

  .right {
    width: 160px;
    text-align: right;
    cursor: pointer;
    color: #da514e;
  }
}

.page-content {
  flex: 1;
  padding: 15px;
}

.search-card {
  margin-bottom: 16px;
}

.search-form {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.table-card {
  margin-top: 16px;
}

.action-link {
  color: #da514e;
  cursor: pointer;
}

.pagination-wrapper {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

.page-footer {
  background: #fff;
  padding: 20px;
}

.footer-content {
  display: flex;
  justify-content: space-between;
  color: #666;
  font-size: 14px;
}

@media (max-width: 768px) {
  .page-header,
  .page-content,
  .page-footer {
    padding: 10px;
  }

  .search-form {
    flex-direction: column;
  }

  .ant-table {
    overflow-x: auto;
  }
}
</style>
